import Icon from "@components/Atoms/Icon";
import { classNames } from "@components/libs/classNames";
import useWatch from "@core-ui/hooks/useWatch";
import { type MouseEvent, type ReactElement, useState } from "react";

export enum NoteType {
	quote = "quote",
	lab = "lab",
	tip = "tip",
	info = "info",
	note = "note",
	danger = "danger",
	hotfixes = "hotfixes",
}

export const noteIcons: { [note in NoteType]: string } = {
	quote: "quote",
	lab: "test-tube-diagonal",
	tip: "lightbulb",
	hotfixes: "wrench",
	info: "info",
	note: "triangle-alert",
	danger: "octagon-alert",
};

interface NoteProps {
	type?: NoteType;
	title?: string;
	children?: ReactElement;
	collapsed?: string | boolean;
	className?: string;
	collapseCallback?: (collapse: boolean) => void;
	disableRender?: boolean;
	isPrint?: boolean;
}

const Note = (props: NoteProps): ReactElement => {
	const { type = NoteType.note, title, className, children, collapseCallback, disableRender, isPrint } = props;
	const collapsed = props.collapsed && !isPrint;
	const [expanded, dispatchExpanded] = useState(!collapsed);

	const toggleExpanded = (e: MouseEvent<HTMLElement>) => {
		e.preventDefault();
		collapseCallback?.(!expanded);
		dispatchExpanded((p) => !p);
	};

	useWatch(() => {
		dispatchExpanded(!collapsed);
		collapseCallback?.(!collapsed);
	}, [collapsed]);

	const clickable = !expanded || collapsed;

	return (
		<div
			className={classNames("admonition", {}, [
				`admonition-${type}`,
				`admonition-${title ? "column" : "row"}`,
				className,
			])}
			data-component="note"
		>
			<div
				className={classNames("admonition-heading", { expanded: !expanded })}
				onClick={clickable ? toggleExpanded : null}
			>
				<div className="admonition-icon">
					{clickable ? (
						<Icon
							code={expanded ? "chevron-down" : "chevron-right"}
							style={{ cursor: "pointer", userSelect: "none" }}
						/>
					) : (
						<Icon code={noteIcons[type]} strokeWidth="2" />
					)}
				</div>
				<div className="titleWrapper" contentEditable={false} suppressContentEditableWarning={true}>
					<div className={classNames("title", { clickable })}>{title}</div>
				</div>
			</div>
			<div className="admonition-content">
				{(disableRender && !expanded) || (
					<div className={classNames("paragraph", { "content-hide": !expanded })}>{children}</div>
				)}
			</div>
		</div>
	);
};

export const noteFoundElementBeforeHighlightHandler = (foundEl: HTMLElement) => {
	const admonition = foundEl.closest(".admonition");
	if (!admonition) return;

	const heading = admonition.querySelector<HTMLElement>(".admonition-heading");
	if (!heading) return;

	const isAlreadyExpanded = !heading.classList.contains("expanded");
	if (isAlreadyExpanded) return;

	const clickableEl = admonition.querySelector<HTMLElement>(".admonition-icon");
	if (!clickableEl) return;

	clickableEl.click();
};

export default Note;
